import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
} from '@chakra-ui/react';

import AddressForm from './AddressForm';
import AddressList from './AddressList';
import AddressShippingOptions from './AddressShippingOptions';
import ShippingOption from './ShippingOption';
import {
  createShopifyAddress,
  getCustomerAddress,
  getShippingOptions,
  saveSubShippingMethod,
  updateShopifyAddress,
} from '../api';
import { transformApiResponseToCustomerAddress } from '../../utils/useFetchCustomerAddress';
import { getIdFromGid } from '../utils';
import { useGlobalContext } from '../state/GlobalContext';
import { useNotifs } from '../../utils/useNotif';
import { useCustomerAddress } from '../state/CustomerAddressContext';
import type { CustomerAddress } from '../state/CustomerAddressContext';
import { isNumber } from 'lodash';
import PickUpList from './PickUpList';

export type shippingOptionType = 'pick-up' | 'ship';

interface IProps {
  subscription: Subscription;
  onUpdate: (res: SaveShippingMethodResponse) => void;
  setDisableOpenButton: (state: boolean) => void;
  setModalOpen: (state: boolean) => void;
  modalOpen: boolean;
}

export type STEPS =
  | 'LIST'
  | 'FORM'
  | 'SHIPPING_OPTIONS'
  | 'CHOOSE_SHIPPING_OPTION'
  | 'CHOOSE_LOCAL_PICKUP';

type FORM_ACTION = 'EDIT' | 'CREATE';

const isValidListAddress = (address: CustomerAddress) => {
  return (
    address.firstName &&
    address.lastName &&
    address.address1 &&
    address.city &&
    address.country
  );
};

export default ({
  subscription,
  onUpdate,
  setDisableOpenButton,
  modalOpen,
  setModalOpen,
}: IProps): React.ReactElement => {
  const { subscriptionId, customerId, authToken, shopDomain } =
    useGlobalContext();
  const { customerAddress, updateCustomerAddress } = useCustomerAddress();

  const { t } = useTranslation();
  const [step, setStep] = useState<STEPS>('LIST');
  const [selectedAddress, setSelectedAddress] = useState<
    ModalFormAddress | undefined
  >(undefined);
  const [editAddressId, setEditAddressId] = useState<CustomerAddress['id']>();
  const [phoneRequired, setPhoneRequired] = useState(false);
  const [formAction, setFormAction] = useState<FORM_ACTION | undefined>();
  const [loading, setLoading] = useState(false);
  const [localPickUpOptions, setLocalPickUpOptions] =
    useState<ShippingOption[]>();
  const [shippingOptions, setShippingOptions] =
    useState<ShippingOptionsResponse>();
  const [shouldSkipShippingView, setShouldSkipShippingView] = useState(false);

  const subAddress =
    subscription.DeliveryMethod?.address ||
    subscription.DeliveryMethod?.pickupOption?.location?.address;
  const currentAddress = customerAddress?.find((address) => {
    return (
      address?.address1 === subAddress?.address1 &&
      address?.zip === subAddress?.zip
    );
  });

  const queryShippingOptions = useCallback(
    async (deliveryAddress: ModalFormAddress) => {
      setLoading(true);
      const shippingOptionsRes = await getShippingOptions(
        customerId,
        subscriptionId,
        authToken,
        { deliveryAddress },
      );
      setShippingOptions(shippingOptionsRes);
      const localPickUpOptions =
        shippingOptionsRes?.deliveryOptions?.deliveryOptions?.filter(
          ({ __typename }) => __typename === 'SubscriptionPickupOption',
        ) || [];

      if (localPickUpOptions.length > 0) {
        setStep('CHOOSE_SHIPPING_OPTION');
        setLocalPickUpOptions(localPickUpOptions);

        setSelectedAddress(
          (localPickUpOptions[0].location as any)?.address as ModalFormAddress,
        );
      } else {
        setStep('LIST');
      }
      setLoading(false);
    },
    [authToken, customerId, subscriptionId],
  );

  useEffect(() => {
    if ((currentAddress || subAddress) && modalOpen) {
      queryShippingOptions(
        (currentAddress as ModalFormAddress) ||
          (subAddress as unknown as ModalFormAddress) ||
          (customerAddress?.[0] as ModalFormAddress),
      );
    }
  }, [
    currentAddress,
    customerAddress,
    modalOpen,
    queryShippingOptions,
    subAddress,
  ]);

  useEffect(() => {
    const submitShippingOption = async () => {
      const { shippingCode, localDeliveryOptions } = getSelectedShippingOptions(
        subscription,
        shippingOptions,
      );

      await handleShippingOptionSubmit(shippingCode, localDeliveryOptions);
    };

    if (shouldSkipShippingView) {
      submitShippingOption();
      setShouldSkipShippingView(false);
    }
  }, [shouldSkipShippingView]);

  const {
    renderNotifs,
    addNotif: addNotifModal,
    resetNotifs: resetNotifsModal,
  } = useNotifs();

  const backToList = () => {
    setSelectedAddress(undefined);
    setEditAddressId(undefined);
    setPhoneRequired(false);
    setFormAction(undefined);
    resetNotifsModal();
  };

  const handleBack = () => {
    if (localPickUpOptions?.length > 0) {
      setStep('CHOOSE_SHIPPING_OPTION');
    } else {
      closeModal();
    }
  };

  const closeModal = () => {
    setSelectedAddress(undefined);
    setEditAddressId(undefined);
    setModalOpen(false);
    setFormAction(undefined);
    resetNotifsModal();
    if (localPickUpOptions?.length > 0) {
      setStep('CHOOSE_SHIPPING_OPTION');
    } else {
      setStep('LIST');
    }
  };

  const handleListEdit = (address: CustomerAddress) => {
    setSelectedAddress(address as ModalFormAddress);
    setEditAddressId(address.id);
    setFormAction('EDIT');
    setStep('FORM');
  };

  const handleNewAddressClick = () => {
    setFormAction('CREATE');
    setStep('FORM');
  };

  const onAddressChange = async (deliveryAddress: ModalFormAddress) => {
    const shippingOptionsRes = await getShippingOptions(
      customerId,
      subscriptionId,
      authToken,
      { deliveryAddress },
    );
    const skipShippingView = isOngoingPrepaid(subscription);

    setShippingOptions(shippingOptionsRes);
    setSelectedAddress(deliveryAddress);

    skipShippingView
      ? setShouldSkipShippingView(true)
      : setStep('SHIPPING_OPTIONS');
  };

  const isOngoingPrepaid = (subscription: Subscription) => {
    if (!subscription.HasPrepaidLines) {
      return false;
    }

    const line = subscription.Lines.find(
      ({ PrepaidV2, PrepaidVersion }) => !!PrepaidV2 || !!PrepaidVersion,
    );
    if (line) {
      const prepaidOrders = line.PrepaidOrders || line.PrepaidV2Orders;
      const prepaidInstance =
        ((line?.SuccessfulBillingAttempts?.length || 0) + 1) % prepaidOrders;

      // If instance is 1, it is prepaid, but not ongoing
      return prepaidInstance !== 1;
    }

    return false;
  };

  const getSelectedShippingOptions = (
    subscription: Subscription,
    shippingOptions: ShippingOptionsResponse,
  ) => ({
    shippingCode: shippingOptions?.deliveryOptions?.deliveryOptions?.[0]?.code,
    localDeliveryOptions: {
      phone: subscription.DeliveryMethod?.localDeliveryOption?.phone || '',
      instructions:
        subscription.DeliveryMethod?.localDeliveryOption?.instructions || '',
    },
  });

  const getNewAddressInfo = async () => {
    if (!customerId) {
      return;
    }

    try {
      const apiResponse = await getCustomerAddress({
        customerId,
        authToken,
      });

      updateCustomerAddress(
        transformApiResponseToCustomerAddress({
          data: apiResponse,
          customerId,
        }),
      );
    } catch (e) {
      console.log('Error getting customer address', e);
    }
  };

  const handleListSubmit = async (
    addressId: CustomerAddress['id'] | undefined,
  ) => {
    const address = customerAddress?.find((address) => address.id == addressId);
    if (address) {
      if (isValidListAddress(address)) {
        try {
          setLoading(true);
          await onAddressChange(address as ModalFormAddress);
        } catch (err) {
          console.error(err);
          addNotifModal(t('subscription.address_update_failed'), {
            type: 'error',
          });
        } finally {
          setLoading(false);
        }
      } else {
        handleListEdit(address);
      }
    }
  };

  const handleAddressFormSubmit = async (deliveryAddress: ModalFormAddress) => {
    try {
      setLoading(true);
      await onAddressChange(deliveryAddress);
    } catch (err) {
      console.error(err);
      addNotifModal(t('subscription.address_update_failed'), {
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleShippingOptionSubmit = async (
    shippingOptionCode: string | undefined,
    localDeliveryOptions?: {
      phone: string | undefined;
      instructions: string | undefined;
    },
  ) => {
    if (!shippingOptionCode) {
      return;
    }

    const address =
      (shippingOptions as any)?.code === shippingOptionCode
        ? (shippingOptions as any).location.address
        : selectedAddress;

    if (shippingOptionCode && shippingOptions && address) {
      try {
        setLoading(true);
        const shipRes = await saveSubShippingMethod(
          customerId,
          subscriptionId,
          authToken,
          {
            draftId: shippingOptions.draftId,
            deliveryAddress: address as ModalFormAddress,
            localDeliveryOptions,
            shippingOptionCode,
          },
        );
        if (formAction === 'EDIT' && editAddressId) {
          const addressId = isNumber(editAddressId)
            ? editAddressId
            : removeParams(getIdFromGid(editAddressId)) || '';

          await updateShopifyAddress({
            address: address,
            addressId,
            customerId,
            authToken,
            shopDomain,
          });
        } else if (formAction === 'CREATE') {
          await createShopifyAddress({
            address: selectedAddress,
            customerId,
            authToken,
            shopDomain,
          });
        }
        setDisableOpenButton(true);
        closeModal();
        if (onUpdate) onUpdate(shipRes);

        await getNewAddressInfo();
      } catch (err) {
        const errorMsg = handleShippingOptionsErrors(err);
        addNotifModal(t(errorMsg), { type: 'error' });

        if (err?.message?.includes('Enter a phone number')) {
          setPhoneRequired(true);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleShippingOptionsErrors = (err: any) => {
    resetNotifsModal();
    console.error(err);
    const errors = {
      'phone is invalid': t('subscription.invalid_phone'),
      'Enter a phone number': t('subscription.phone_required'),
      'address zip is invalid': t('subscription.invalid_zip'),
      'Country/region not supported': t('subscription.invalid_area'),
    };

    const errorMsg = err?.message || '';
    let errorText = t('subscription.address_update_failed');

    if (errorMsg) {
      for (const [key, value] of Object.entries(errors)) {
        if (errorMsg.includes(key)) {
          errorText = value;
          break;
        }
      }
    }

    return errorText;
  };

  const shippingOptionType: shippingOptionType = subscription.DeliveryMethod
    ?.pickupOption
    ? 'pick-up'
    : 'ship';

  return loading ? (
    <Modal isOpen={modalOpen} onClose={closeModal} isCentered>
      <ModalOverlay className="awt-shipping-address-modal--overlay">
        <ModalContent className="awt-shipping-address-modal">
          <ModalHeader>{t('subscription.update_shipping')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HStack justifyContent="center" alignItems="center">
              <Spinner size="lg" />
            </HStack>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  ) : (
    <>
      <Modal isOpen={modalOpen} onClose={closeModal} isCentered>
        <ModalOverlay className="awt-shipping-address-modal--overlay">
          <ModalContent className="awt-shipping-address-modal">
            {renderNotifs()}

            {step === 'CHOOSE_SHIPPING_OPTION' && (
              <ShippingOption
                loading={loading}
                setStep={setStep}
                onCancel={closeModal}
                shippingOptionType={shippingOptionType}
              />
            )}

            {step === 'CHOOSE_LOCAL_PICKUP' && (
              <PickUpList
                localPickUpOptions={localPickUpOptions}
                loading={loading}
                onSubmit={handleShippingOptionSubmit}
                handleBack={handleBack}
              />
            )}

            {step === 'LIST' && (
              <AddressList
                loading={loading}
                initialSelected={currentAddress?.id}
                onSubmit={handleListSubmit}
                onEdit={handleListEdit}
                onNew={handleNewAddressClick}
                handleBack={handleBack}
              />
            )}

            {step === 'FORM' && (
              <AddressForm
                address={selectedAddress}
                loading={loading}
                onCancel={closeModal}
                onSubmit={handleAddressFormSubmit}
              />
            )}

            {step === 'SHIPPING_OPTIONS' && (
              <AddressShippingOptions
                shippingOptions={shippingOptions}
                loading={loading}
                onCancel={phoneRequired ? backToList : closeModal}
                isPhoneRequired={phoneRequired}
                onSubmit={handleShippingOptionSubmit}
                subscription={subscription}
              />
            )}
          </ModalContent>
        </ModalOverlay>
      </Modal>
    </>
  );
};

const removeParams = (id: string | undefined): string => {
  return id?.split('?')[0] || '';
};
